import { useState, useEffect } from 'react';
import { history, IRouteComponentProps } from 'umi';
import { useSelector } from 'dva';
import { message as iglooMessage } from 'iglooform';
import { BackButton } from '@/components/back-button/back-button';
import WithdrawalSuccessfulPage from '@/components/withdrawal-successful';
import { aesDecrypt } from '@/utils/common';
import { goWithdraw, getAccounts, getCommissionBalance } from '../service';
import throttle from 'lodash/throttle';
import SkeletonLoading from '@/components/skeleton-loading';
import CashOut from '@/components/cash-out/cash-out';

const CashOutPage = ({ location }: IRouteComponentProps) => {
  const { query } = location;
  const { balance } = query;
  const [_balance, set_balance] = useState<number>(0);
  const [_minWithdrawalAmount, set_minWithdrawalAmount] = useState<number>(0);
  const [account, setAccount] = useState<Account>({} as Account);
  const [amount, setAmount] = useState<number>(0); // 用于计算的amount
  const [loading, setLoading] = useState<boolean>(false);
  const [amountStr, setAmountStr] = useState<string>(
    aesDecrypt(balance as string),
  ); // 用于输入框展示的amount
  const [showSuccessPage, setSuccessPage] = useState<boolean>(false);
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [resultData, setResultData] = useState<IWithdrawalSuccessful>(
    {} as IWithdrawalSuccessful,
  );
  let { currentLang } = useSelector<ReduxState, GlobalState>(
    (state) => state.global,
  );

  currentLang = localStorage.getItem('selected_lang') ?? currentLang;

  useEffect(() => {
    setLoading(true);
    getAccounts().then((res) => {
      if (res) {
        const accounts = res.accounts ?? [];
        if (accounts.length) setAccount(accounts[0]);
      }
    });
    getCommissionBalance().then((res) => {
      if (res) {
        setAmount(Number(res.available));
        setAmountStr(res.available);
        set_balance(res.available);
        set_minWithdrawalAmount(Number(res.min_withdraw_amount));
      }
    });
    setLoading(false);
  }, []);

  // 输入监听
  const handleAmountChange = (e: any) => {
    setAmountStr(handleFormatter(e.target.value));
    setAmount(+handleParser(e.target.value.toString()));
  };

  // 格式化输入
  const handleFormatter = (value: number | string): string => {
    if (!value) return '';
    value = handleParser(value.toString());
    if (currentLang === 'vi-VN') {
      return value.toString().replace(/(\d)(?=(?:\d{3})+$)/g, `$1.`);
    }
    return value.toString().replace(/(\d)(?=(?:\d{3})+$)/g, `$1,`);
  };

  // 转回数字
  const handleParser = (value: string): string => {
    return value.includes(',')
      ? value.replace(/\$\s?|(,*)/g, '')
      : value.replace(/\$\s?|(\.*)/g, '');
  };

  // 计算net amount
  const setNetAmount = () => {
    if (amount > _balance) return 0;
    const net = amount * 0.9;
    if (isNaN(net)) {
      return '';
    }
    return Math.floor(net);
  };

  // 提现
  const handleWithdrawal = throttle(
    () => {
      // withdrawal 埋点
      window.gtag('event', 'cash_out', {
        user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')
          ?.id,
      });
      const netAmount = setNetAmount();
      if (netAmount < _minWithdrawalAmount) {
        const message =
          currentLang === 'en-US'
            ? `The minimum amount for withdrawal is ${_minWithdrawalAmount} VND(After tax deduction), please make sure your total amount is above 50,000 VND and try again`
            : `Số dư tối thiểu để thực hiện rút tiền là ${_minWithdrawalAmount
                .toString()
                .replace(
                  ',',
                  '.',
                )} đồng(Sau khi trừ thuế). Vui lòng kiểm tra và thực hiện lại.`;
        iglooMessage.warning(message);
        return;
      }
      setBtnLoading(true);
      goWithdraw({ amount: Number(amount), currency: 'VND' })
        .then((res) => {
          if (res) {
            setResultData(res);
            setSuccessPage(true);
          }
        })
        .finally(() => {
          setBtnLoading(false);
        });
    },
    500,
    { leading: true, trailing: false },
  );

  if (showSuccessPage && !btnLoading) {
    return <WithdrawalSuccessfulPage resultData={resultData} />;
  }

  return (
    <>
      <BackButton handleClick={() => history.goBack()} />
      {!loading ? (
        <CashOut
          account={account}
          currentLang={currentLang}
          handleFormatter={handleFormatter}
          amountStr={amountStr}
          handleAmountChange={handleAmountChange}
          setAmountStr={setAmountStr}
          _balance={_balance}
          setAmount={setAmount}
          amount={amount}
          handleWithdrawal={handleWithdrawal}
          btnLoading={btnLoading}
        />
      ) : (
        <div style={{ marginTop: '45px' }}>
          <SkeletonLoading />
        </div>
      )}
    </>
  );
};

export default CashOutPage;
